import { Box, Flex } from '@chakra-ui/react'
import { ReactNode, useRef } from 'react'
import { ScrollContext } from './ScrollContext'

// Fallback header height estimate, used only before AppHeader's
// ResizeObserver publishes a real measurement (first paint / pages
// that render no AppHeader). The live value comes from
// --app-header-height, set by AppHeader — see headerOffsetCss() below
// and paire-wiki/Frontend/Fixed Header Offset.md.
// Base: 48px logo + 24px padding (py=3) = 72px
// lg: 64px logo + 32px padding (py=4) = 96px
export const HEADER_HEIGHT = {
  base: '72px',
  lg: '96px',
}

/**
 * Shared CSS expression for "below the header, plus an optional extra
 * offset" — the single formula every header-consuming layout must use,
 * so the double-count-safe fallback pattern exists in exactly one
 * place. Before AppHeader's first measurement (or on a page that
 * renders no AppHeader while still requesting header spacing),
 * --app-header-height is unset and the fallback branch reproduces
 * today's static estimate + safe-area-inset as two separate terms —
 * matching the pre-D59 formula exactly. Once AppHeader publishes a
 * measurement, that value already includes the safe-area inset, so it
 * is used as-is with no second inset addition.
 */
export function headerOffsetCss(size: 'base' | 'lg', extra?: string): string {
  const fallback = `calc(${HEADER_HEIGHT[size]} + env(safe-area-inset-top))`
  const measuredOrFallback = `var(--app-header-height, ${fallback})`
  return `calc(${measuredOrFallback} + ${extra ?? '0px'})`
}

interface PageWrapperProps {
  children: ReactNode
  /** Background color for the page content area. Defaults to transparent (uses global GradientBackground) */
  bg?: string
  /** Whether content should scroll. Defaults to true */
  scrollable?: boolean
  /** Additional padding at bottom for tab bar. Defaults to 80px */
  bottomPadding?: string
  /** Whether the page has a fixed header that needs spacing. Defaults to true */
  hasFixedHeader?: boolean
  /** Custom styles for the content area */
  contentProps?: Record<string, unknown>
}

/**
 * PageWrapper - A Chakra-based container
 *
 * Provides a consistent page layout with:
 * - Full viewport height
 * - Scrollable content area
 * - Bottom padding for tab navigation
 * - Header spacing via CSS calc()
 * - Dark theme background by default
 *
 * Usage:
 * ```tsx
 * <PageWrapper>
 *   <AppHeader />
 *   <Box>Your content here</Box>
 * </PageWrapper>
 * ```
 */
export function PageWrapper({
  children,
  bg = 'transparent',
  scrollable = true,
  bottomPadding = '80px',
  hasFixedHeader = true,
  contentProps = {},
}: PageWrapperProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Calculate padding-top based on the measured (or fallback) header height
  const getPaddingTop = (size: 'base' | 'lg') => {
    if (!hasFixedHeader) {
      return 'env(safe-area-inset-top)'
    }
    return headerOffsetCss(size)
  }

  return (
    <ScrollContext.Provider value={{ scrollContainerRef }}>
      <Flex
        direction="column"
        h="100dvh"
        w="100%"
        bg={bg}
        color="white"
        position="relative"
        zIndex={1}
      >
        <Box
          ref={scrollContainerRef}
          // Scopes the "scrollbar track starts below the fixed AppHeader"
          // rule in src/index.css. Deliberately NOT keyed off hasFixedHeader:
          // that prop only says whether *PageWrapper* pads for the header
          // (Search, ListDetails loading states, etc. pass `false` yet still
          // render AppHeader and pad themselves). The CSS reads
          // --app-header-height directly instead, which AppHeader publishes
          // while mounted and removes on unmount, so a page with no header
          // (e.g. OnboardingFindFriends) naturally resolves to a 0px offset.
          data-scrollbar-below-header=""
          flex="1"
          overflowY={scrollable ? 'auto' : 'hidden'}
          overflowX="hidden"
          pb={bottomPadding}
          pt={{ base: getPaddingTop('base'), lg: getPaddingTop('lg') }}
          sx={{
            // Scrollbar styling: the global ghost-scrollbar rule in
            // src/index.css covers this container — no local override needed.
            // Smooth scrolling
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }}
          {...contentProps}
        >
          {children}
        </Box>
      </Flex>
    </ScrollContext.Provider>
  )
}

export default PageWrapper
